import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  TextField,
  TextArea,
  RadioButtons,
  SelectField,
  FieldGroup,
  Label,
  DateField,
  Checkboxes,
  LargeTextArea,
  MultiTextField,
  DefaultDateField,
  MultiSelectField,
  NumberField,
  ExternalVideo,
  PasswordField
} from "../../forms"
import { P } from "../../typography"
import styled, { css } from "styled-components"

const fieldTypes = {
  text: TextField,
  textarea: TextArea,
  radio: RadioButtons,
  select: SelectField,
  date: DateField,
  default_date: DefaultDateField,
  checkbox: Checkboxes,
  large_textarea: LargeTextArea,
  multi_text: MultiTextField,
  url: TextField,
  multi_select: MultiSelectField,
  number: NumberField,
  external_video: ExternalVideo,
  password: PasswordField
}

const maxLength = 99999

const retrieveFieldType = (fieldType, customComponents) => {
  if (Object.keys(customComponents).includes(fieldType)) return customComponents[fieldType]
  else if (Object.keys(fieldTypes).includes(fieldType)) return fieldTypes[fieldType]
  else return fieldTypes["text"]
}

const CharP = styled(P)`
  margin-top: 0.25rem;

  ${({ simpleForm }) =>
    !!simpleForm &&
    css`
      font-weight: 400;
      font-size: 0.9rem;
      color: ${({ theme }) => theme.colors.fields.hint};
      margin-top: 0.5rem;
    `}
`

const Field = ({
  name,
  label,
  required,
  invalid,
  field_options,
  field_type,
  character_limit,
  dependent_on,
  dependent_value,
  stateValue,
  handleInputChange,
  show,
  fieldsetLegend,
  customFieldComponents,
  hint,
  simpleForm,
  loaded,
  handleRemoveVideo
}) => {
  const FieldType = retrieveFieldType(field_type, customFieldComponents)
  const [invalidValue, setInvalid] = useState(invalid)
  let options = field_options.map((option) => [option.name, option.name])

  if (field_type === "multi_select") {
    options = field_options.map((option) => ({
      label: option.name,
      value: option.name
    }))
    stateValue = stateValue === "" ? [] : stateValue
  } else {
    stateValue = !!stateValue ? stateValue : ""
  }

  const onInputChange = (fieldName, value, type = null) => {
    if (handleInputChange !== undefined) handleInputChange(fieldName, value, type)
    if (character_limit) calculateCharactersRemaining(value.length)
    if (invalidValue) setInvalid(false)
  }

  const [charactersRemaining, setCharactersRemaining] = useState(character_limit - stateValue.length)
  const calculateCharactersRemaining = (characterCount) => setCharactersRemaining(character_limit - characterCount)
  const showCharacterLimit = () => {
    if (!["text", "textarea", "large_textarea"].includes(field_type)) return false
    if (parseInt(character_limit).isNaN) return false
    if (parseInt(character_limit) < 1) return false
    return true
  }

  let showLabel = true
  if (label === fieldsetLegend) showLabel = false

  return (
    <FieldGroup
      show={show}
      data-dependent-on={dependent_on}
      data-dependent-value={dependent_value}
      simpleForm={simpleForm}
    >
      {showLabel && (
        <Label invalid={invalidValue} simpleForm={simpleForm}>
          {label}
          {!!required && " *"}
        </Label>
      )}
      <FieldType
        name={name}
        multiSelect={field_type === "multi_select"}
        value={stateValue}
        placeholder={dependent_on ? label : null}
        required={dependent_on ? false : required}
        requiredField={required}
        options={options}
        maxLength={character_limit < 1 ? maxLength : character_limit}
        handleInputChange={onInputChange}
        invalid={invalidValue}
        hint={hint}
        simpleForm={simpleForm}
        loaded={loaded}
        handleRemoveVideo={handleRemoveVideo}
      />
      {showCharacterLimit() && (
        <CharP
          simpleForm={simpleForm}
          align="right"
          size="small"
          marginless
        >{`${charactersRemaining} characters remaining`}</CharP>
      )}
    </FieldGroup>
  )
}

Field.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  invalid: PropTypes.bool,
  field_options: PropTypes.array,
  field_type: PropTypes.string,
  character_limit: PropTypes.number,
  dependent_on: PropTypes.string,
  dependent_value: PropTypes.string,
  stateValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  handleInputChange: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  customFieldComponents: PropTypes.object
}

Field.defaultProps = {
  name: "",
  label: "",
  required: false,
  invalid: false,
  field_options: [],
  field_type: "text",
  character_limit: 0,
  dependent_on: "",
  dependent_value: "Yes",
  stateValue: "",
  handleInputChange: undefined,
  show: false,
  customFieldComponents: {}
}

export default Field
